import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase-client';

export default function EventRegister({ teamId, form }) {
  const [includeEmergencyContacts, setIncludeEmergencyContacts] = useState(false);
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [registerLayout, setRegisterLayout] = useState('compact');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [exportColumns, setExportColumns] = useState(form.attendanceMode === 'rsvp' ? 'sourceName, occurrence, attendee, attendanceStatus' : 'sourceName, occurrence, attendee, bookingContact, ticketType, quantity, extras, paymentStatus, refundStatus, bookingDate');
  const operationsSourceId = form.originalEventId || form.id;
  const downloadCsv = async () => {
    const result = await httpsCallable(
      functions,
      'exportBookingCsv',
    )({
      teamId,
      sourceType: 'event',
      sourceId: operationsSourceId,
      occurrenceId: form.occurrenceId || '',
      includeSensitive,
      includeEmergencyContacts,
      includeCancelled,
      columns: `${exportColumns}${includeEmergencyContacts ? ', emergencyContacts' : ''}`
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    });
    const blob = new Blob([result.data.csv], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = result.data.filename;
    link.click();
    URL.revokeObjectURL(url);
  };
  const printRegister = async () => {
    const popup = window.open('', '_blank');
    if (!popup) throw new Error('Allow pop-ups to print the register.');
    popup.opener = null;
    popup.document.write('<!doctype html><title>Preparing register…</title><p style="font-family:Arial,sans-serif;padding:24px">Preparing the A4 register…</p>');
    popup.document.close();
    const escape = (value) =>
      String(value ?? '').replace(
        /[&<>"']/g,
        (char) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          })[char],
      );
    try {
      const result = await httpsCallable(
        functions,
        'generateSafetyRegister',
      )({
        teamId,
        sourceType: 'event',
        sourceId: operationsSourceId,
        occurrenceId: form.occurrenceId || '',
        includeSensitive,
        includeEmergencyContacts,
        includeCancelled,
        layout: registerLayout,
      });
      const register = result.data;
      const rows = register.rows.length
        ? register.rows.map((row) => `<section><h2>${escape(row.attendee)}</h2>${row.occurrence ? `<p>${escape(row.occurrence)}</p>` : ''}${row.attendanceStatus ? `<p>${escape(row.attendanceStatus)}</p>` : `<p>Booking contact: ${escape(row.bookingContact)}</p><p>Ticket: ${escape(row.ticketType)}</p><p>Status: ${escape(row.paymentStatus)}</p>`}${row.emergencyContacts?.length ? `<p>Emergency contacts: ${escape(row.emergencyContacts.map((contact) => [contact.name, contact.relationship ? `(${contact.relationship})` : '', contact.phone, contact.alternatePhone, contact.email].filter(Boolean).join(' ')).join('; '))}</p>` : ''}${row.safetyAnswers ? `<pre>${escape(JSON.stringify(row.safetyAnswers, null, 2))}</pre>` : ''}</section>`).join('')
        : '<section><h2>No confirmed participants</h2><p>There are no confirmed attendees to include in this register.</p></section>';
      popup.document.open();
      popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escape(register.title)}</title><style>@page{size:A4;margin:12mm}body{font-family:Arial,sans-serif}.meta{border-bottom:2px solid;margin-bottom:10px}section{padding:8px;border-bottom:1px solid #bbb}.participant_pages section{break-after:page;border:0}pre{white-space:pre-wrap}</style></head><body class="${escape(register.layout)}"><div class="meta"><h1>${escape(register.title)}</h1><p>${escape(register.occurrence)} · Generated ${escape(new Date(register.generatedAt).toLocaleString())}</p><strong>${escape(register.confidentiality)}</strong></div>${rows}</body></html>`);
      popup.document.close();
      window.setTimeout(() => {
        popup.focus();
        popup.print();
      }, 150);
    } catch (error) {
      popup.close();
      throw error;
    }
  };

  const run = async (action) => {
    setBusy(true);
    setError('');
    try { await action(); } catch (err) { setError(err.message || 'Could not create the register. Please try again.'); } finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <strong>Event register</strong>
      <p className="text-muted small">Download the confirmed attendee list or print it to save as PDF. {form.occurrenceId ? 'Includes this date only.' : form.type === 'recurring' ? 'Includes recorded dates and series responses.' : ''}</p>
      {form.attendanceMode !== 'rsvp' && form.attendanceMode !== 'ticketed' ? <p>This event does not track attendees. Enable RSVPs or ticketing to create a register.</p> : <>
        <label className="checkbox-row"><input type="checkbox" checked={includeEmergencyContacts} onChange={(e) => setIncludeEmergencyContacts(e.target.checked)} /> Include emergency contacts</label>
        <small className="text-muted">Emergency contacts require the Sensitive booking answers permission. Access is recorded. Missing contacts are left blank.</small>
        {form.attendanceMode === 'ticketed' && <>
          <label>CSV columns (comma separated)<input className="input" value={exportColumns} onChange={(e) => setExportColumns(e.target.value)} /></label>
          <label className="checkbox-row"><input type="checkbox" checked={includeSensitive} onChange={(e) => setIncludeSensitive(e.target.checked)} /> Include protected booking answers in the printed register</label>
          <label className="checkbox-row"><input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} /> Include refunded attendees</label>
        </>}
        <label>Register layout<select className="input" value={registerLayout} onChange={(e) => setRegisterLayout(e.target.value)}><option value="compact">Compact roster</option><option value="participant_pages">One participant per page</option></select></label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => run(downloadCsv)}>Download register CSV</button>
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => run(printRegister)}>Print / save register PDF</button>
        </div>
      </>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
